// Single source of truth for supported UI locales.
// Add a new language here and the switchers/formatters pick it up automatically.

export type LocaleCode = 'en' | 'th' | 'fil' | 'vi' | 'ja' | 'id' | 'ban';

export interface LocaleOption {
  code: LocaleCode;
  /** Display name shown in language switchers (native name where practical). */
  name: string;
  /** Flag emoji shown next to the name. */
  flag: string;
}

// Display order used by every language switcher in the app.
export const SUPPORTED_LOCALES: LocaleOption[] = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'fil', name: 'Filipino', flag: '🇵🇭' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ban', name: 'Basa Bali', flag: '🇮🇩' },
];

export const DEFAULT_LOCALE: LocaleCode = 'en';

// BCP-47 locale tags used for Intl date/number/currency formatting.
// Balinese (ban) has no broadly-supported Intl data, so it reuses Indonesian
// conventions, which match how dates/numbers are written in Bali.
const INTL_LOCALE_MAP: Record<LocaleCode, string> = {
  en: 'en-US',
  th: 'th-TH',
  fil: 'fil-PH',
  vi: 'vi-VN',
  ja: 'ja-JP',
  id: 'id-ID',
  ban: 'id-ID',
};

export function isLocaleCode(value: unknown): value is LocaleCode {
  return typeof value === 'string' && SUPPORTED_LOCALES.some((l) => l.code === value);
}

/**
 * Resolve an i18next language string (e.g. "en", "en-US", "ban") to one of the
 * supported locale codes. Falls back to the default locale when unknown.
 */
export function detectLocale(lang?: string | null): LocaleCode {
  if (!lang) return DEFAULT_LOCALE;
  const lower = lang.toLowerCase();
  const exact = SUPPORTED_LOCALES.find((l) => l.code === lower);
  if (exact) return exact.code;
  const prefixed = SUPPORTED_LOCALES.find((l) => lower.startsWith(l.code));
  if (prefixed) return prefixed.code;
  return DEFAULT_LOCALE;
}

/** Map a locale code to a BCP-47 tag suitable for the Intl APIs. */
export function getIntlLocale(code: LocaleCode): string {
  return INTL_LOCALE_MAP[code] ?? 'en-US';
}

export function getLocaleOption(code: LocaleCode): LocaleOption {
  return SUPPORTED_LOCALES.find((l) => l.code === code) ?? SUPPORTED_LOCALES[0];
}
